import React, { memo } from "react";
import { Link } from "react-router-dom";
import { getProductPlaceholder } from "../lib/productPlaceholder";

export interface ProductListCardProps {
  p: any;
  isFavorite: boolean;
  customerPrice: string | undefined;
  canAddToCart: boolean;
  hasUser: boolean;
  userIsActive: boolean;
  rawInput: string;
  addingToCart: boolean;
  addedToCart: boolean;
  requestingProduct: boolean;
  productRequested: boolean;
  onToggleFavorite: (id: number) => void;
  onAddToCart: (e: React.MouseEvent, id: number) => void;
  onStepQty: (id: number, delta: number) => void;
  onSetRaw: (id: number, val: string) => void;
  onCommitRaw: (id: number) => void;
  onRequestSpecialOrder: (e: React.MouseEvent, id: number) => void;
  onNavigateSignup: () => void;
  gridCols: string;
}

function ProductListCardInner({
  p,
  isFavorite,
  customerPrice,
  canAddToCart,
  hasUser,
  userIsActive,
  rawInput,
  addingToCart,
  addedToCart,
  requestingProduct,
  productRequested,
  onToggleFavorite,
  onAddToCart,
  onStepQty,
  onSetRaw,
  onCommitRaw,
  onRequestSpecialOrder,
  onNavigateSignup,
  gridCols,
}: ProductListCardProps) {
  return (
    <div className={`grid grid-cols-1 items-center ${gridCols} gap-2 md:gap-3 px-4 py-3 hover:bg-blue-50/40 transition-colors group`}>
      {hasUser && (
        userIsActive ? (
          <button
            onClick={() => onToggleFavorite(p.id)}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            className="hidden md:flex items-center justify-center w-7 h-7 rounded-full hover:bg-red-50 transition-colors"
          >
            <svg className={`w-4 h-4 transition-colors ${isFavorite ? "text-red-500" : "text-gray-300 hover:text-red-300"}`} viewBox="0 0 24 24" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </button>
        ) : (
          <span className="hidden md:block" />
        )
      )}

      <Link to={`/products/${p.id}`} className="contents">
        <div className="hidden md:flex w-10 h-10 rounded bg-gray-50 border border-gray-100 items-center justify-center flex-shrink-0 overflow-hidden">
          <img
            src={p.imageUrl || getProductPlaceholder(p.id, p.dosageForm, p.route)}
            alt={[p.name, p.strength, p.dosageForm].filter(Boolean).join(" ")}
            className="w-full h-full object-contain"
            onError={(e) => { e.currentTarget.src = getProductPlaceholder(p.id, p.dosageForm, p.route); }}
          />
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-medium text-primary group-hover:text-primary/80 truncate leading-tight">{p.name}</h3>
          <div className="md:hidden text-xs text-gray-500 mt-0.5">
            NDC: {p.ndc} &middot; {p.manufacturer}
          </div>
          {p.dosageForm && <span className="hidden md:inline text-xs text-gray-400">{p.dosageForm}{p.route ? ` · ${p.route}` : ""}</span>}
        </div>

        <span className="hidden md:block text-xs text-gray-600 font-mono">{p.ndc}</span>
        <span className="hidden md:block text-xs text-gray-600">{p.strength || "—"}{p.packageSize ? ` / ${p.packageSize}` : ""}</span>
        <span className="hidden md:block text-xs text-gray-600 truncate">{p.manufacturer || "—"}</span>

        <div className="hidden md:flex items-center">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
            {p.inStock ? "In Stock" : "2-Day Delivery"}
          </span>
        </div>

        <div className="hidden md:block text-right">
          {customerPrice !== undefined ? (
            <span className="text-sm font-semibold text-green-600">${customerPrice}</span>
          ) : p.price && parseFloat(p.price) > 0 ? (
            <span className="text-sm font-semibold text-accent">${p.price}</span>
          ) : p.price !== null && p.price !== undefined ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : hasUser && userIsActive ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : (
            <span className="text-xs text-accent hover:underline cursor-pointer" onClick={(e) => { e.preventDefault(); onNavigateSignup(); }}>Sign up</span>
          )}
        </div>
      </Link>

      {canAddToCart && (
        <div className="hidden md:flex flex-col items-center justify-center gap-1">
          {p.inStock ? (
            <>
              <div className="flex items-center gap-1">
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, -1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                <input
                  type="number"
                  min={1}
                  value={rawInput}
                  onChange={e => { onSetRaw(p.id, e.target.value); }}
                  onBlur={() => onCommitRaw(p.id)}
                  onFocus={e => e.target.select()}
                  onClick={e => e.stopPropagation()}
                  className="w-10 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, +1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
              </div>
              <button
                onClick={(e) => onAddToCart(e, p.id)}
                disabled={addingToCart}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all duration-200 ${
                  addedToCart
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-accent text-white hover:bg-accent/90 shadow-sm"
                }`}
              >
                {addingToCart ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : addedToCart ? "Added!" : "+ Cart"}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={(e) => onRequestSpecialOrder(e, p.id)}
                disabled={requestingProduct || productRequested}
                className={`text-[10px] font-medium px-2 py-1 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  productRequested
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                }`}
                title="Availability may vary"
              >
                {requestingProduct ? "..." : productRequested ? "Requested!" : "Request – 2 Day"}
              </button>
              <span className="text-[9px] text-gray-400 leading-tight">Availability may vary</span>
            </div>
          )}
        </div>
      )}

      <div className="md:hidden flex items-center justify-between mt-1">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
            {p.inStock ? "In Stock" : "2-Day Delivery"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {customerPrice !== undefined ? (
            <span className="text-sm font-semibold text-green-600">${customerPrice}</span>
          ) : p.price && parseFloat(p.price) > 0 ? (
            <span className="text-sm font-semibold text-accent">${p.price}</span>
          ) : p.price !== null && p.price !== undefined ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : hasUser && userIsActive ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : (
            <span className="text-xs text-accent hover:underline cursor-pointer" onClick={onNavigateSignup}>Sign up for pricing</span>
          )}
          {canAddToCart && p.inStock && (
            <div className="flex flex-col items-end gap-1" onClick={e => e.preventDefault()}>
              <div className="flex items-center gap-1">
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, -1); }} className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                <input
                  type="number"
                  min={1}
                  value={rawInput}
                  onChange={e => { onSetRaw(p.id, e.target.value); }}
                  onBlur={() => onCommitRaw(p.id)}
                  onFocus={e => e.target.select()}
                  onClick={e => e.stopPropagation()}
                  className="w-8 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, +1); }} className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
              </div>
              <button
                onClick={(e) => onAddToCart(e, p.id)}
                disabled={addingToCart}
                className={`text-xs px-3 py-1 rounded-lg font-medium transition-all ${
                  addedToCart ? "bg-green-50 text-green-700 border border-green-200" : "bg-accent text-white hover:bg-accent/90"
                }`}
              >
                {addingToCart ? "..." : addedToCart ? "Added!" : "+ Cart"}
              </button>
            </div>
          )}
          {canAddToCart && !p.inStock && (
            <button
              onClick={(e) => onRequestSpecialOrder(e, p.id)}
              disabled={requestingProduct || productRequested}
              className={`text-xs px-2 py-1 rounded-lg font-medium transition-all ${
                productRequested ? "bg-green-50 text-green-700 border border-green-200" : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
            >
              {requestingProduct ? "..." : productRequested ? "Requested!" : "Request – 2 Day"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const ProductListCard = memo(ProductListCardInner);
